import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import {
    Alert,
    Button,
    Card,
    Col,
    Layout,
    message,
    Row,
    Space,
    Spin,
} from "antd";
import WorkbenchNewActivity from "../components/WorkbenchNewActivity";
import { FileTextOutlined } from "@ant-design/icons";
import { WbDocument, WbSubject } from "../../workbench/types/WbTypes";
import { useWorkbenchStore } from "../../workbench/stores/context";
import WorkbenchNewDocumentPreviewDrawer from "../components/WorkbenchNewDocumentPreviewDrawer";
import WorkbenchNewSubjectHeader from "../components/WorkbenchNewSubjectHeader";
import WorkbenchNewSubjectSummary from "../components/WorkbenchNewSubjectSummary";
import WorkbenchNewSubjectTabs from "../components/WorkbenchNewSubjectTabs";
import WorkbenchNewSubjectTopPanel from "../components/WorkbenchNewSubjectTopPanel";
import { saveLastVisitedSubject } from "../utils/lastVisitedSubject";
import {
    copyTextToClipboard,
    formatActivityTime,
    fallbackActions,
    fallbackSubjectView,
    getSubjectParty,
    getSubjectTitle,
    noop,
} from "../utils/workbenchNewSubjectView";

type LocationState = {
    subject?: WbSubject;
};

const getLatestUploadedDocument = (documents: WbDocument[]) =>
    documents.reduce<WbDocument | null>((latestDocument, document) => {
        const uploadTime = Date.parse(document.uploadTime);

        if (Number.isNaN(uploadTime)) {
            return latestDocument;
        }

        if (!latestDocument) {
            return document;
        }

        const latestUploadTime = Date.parse(latestDocument.uploadTime);

        return Number.isNaN(latestUploadTime) || uploadTime > latestUploadTime
            ? document
            : latestDocument;
    }, null);

const getDocumentViewerUrl = (document: WbDocument) =>
    `/document-viewer/${encodeURIComponent(
        document.packageId,
    )}?fileName=${encodeURIComponent(document.fileName || "document")}`;

const WorkbenchNewSubjectPage: React.FC = () => {
    const location = useLocation();
    const { subjectId } = useParams<"subjectId">();
    const { workbenchStore } = useWorkbenchStore();
    const routeSubject = (location.state as LocationState | null)?.subject;
    const [previewDocument, setPreviewDocument] = useState<WbDocument | null>(
        null,
    );

    useEffect(() => {
        void workbenchStore.loadInitialData();
    }, [workbenchStore]);

    useEffect(() => {
        if (!subjectId) {
            return;
        }

        void workbenchStore.loadSubjectById(subjectId, routeSubject);
    }, [routeSubject, subjectId, workbenchStore]);

    const subject = workbenchStore.subjectData ?? routeSubject ?? null;

    useEffect(() => {
        if (subject) {
            saveLastVisitedSubject(subject);
        }
    }, [subject]);

    const title = getSubjectTitle(subject);
    const broker = subject?.context?.broker ?? "";
    const cedent = getSubjectParty(subject);
    const claimant = subject?.context?.claimant ?? "";
    const identifier = subject?.identifier ?? fallbackSubjectView.identifier;
    const uwYear = subject?.context?.uwYear ?? fallbackSubjectView.uwYear;
    const mainClass = subject?.context?.mainClass ?? "N/A";
    const documents = workbenchStore.documentsData;
    const latestUploadedDocument = useMemo(
        () => getLatestUploadedDocument(documents),
        [documents],
    );
    const tasks = workbenchStore.tasksData;
    const actions = workbenchStore.actionsHistoryData.length
        ? workbenchStore.actionsHistoryData
        : fallbackActions;

    const handleShare = async () => {
        const shareUrl = subjectId
            ? `${window.location.origin}/workbench-new/${encodeURIComponent(
                  subjectId,
              )}`
            : window.location.href;

        try {
            await copyTextToClipboard(shareUrl);
            message.success(`Link copied for ${identifier || "this subject"}`);
        } catch (error) {
            console.error("Failed to copy subject link.", error);
            message.error("Could not copy the subject link");
        }
    };

    if (!subjectId) {
        return (
            <Layout className="screen-size workbench-new-layout">
                <Alert
                    message="Subject route is missing an identifier."
                    type="error"
                />
            </Layout>
        );
    }

    return (
        <Layout className="screen-size workbench-new-layout">
            <Layout.Content className="workbench-new-subject-main">
                <WorkbenchNewSubjectHeader
                    broker={broker}
                    identifier={identifier}
                    onFollow={noop}
                    onMore={noop}
                    onShare={handleShare}
                    subject={subject}
                    title={title}
                    uwYear={uwYear}
                />

                {workbenchStore.subjectInfoWidget.isLoading && !subject && (
                    <div className="workbench-new-subject-loading">
                        <Spin />
                        <span>Loading subject...</span>
                    </div>
                )}

                {workbenchStore.subjectInfoWidget.error && (
                    <Alert
                        className="workbench-new-subject-alert"
                        description="Showing available readonly information where possible."
                        message={workbenchStore.subjectInfoWidget.error}
                        showIcon={true}
                        type="warning"
                    />
                )}

                {subject?.kind === "Contract" && (
                    <WorkbenchNewSubjectTopPanel
                        subject={subject}
                        isSaving={
                            workbenchStore.isStageSaving ||
                            workbenchStore.subjectInfoWidget.isLoading
                        }
                        needsRefresh={
                            workbenchStore.stageNeedsRefresh ||
                            !!workbenchStore.subjectInfoWidget.error
                        }
                        error={
                            workbenchStore.stageError ??
                            workbenchStore.subjectInfoWidget.error
                        }
                        onRefresh={() => {
                            void workbenchStore.refreshContractStage();
                        }}
                        onChange={(stage) => {
                            void workbenchStore
                                .changeContractStage(stage)
                                .then((saved) => {
                                    if (saved)
                                        message.success("Contract stage saved");
                                });
                        }}
                    />
                )}

                <Row
                    className="workbench-new-subject-overview"
                    gutter={[18, 18]}
                >
                    <Col xs={24} xl={15}>
                        <WorkbenchNewSubjectSummary
                            broker={broker}
                            caseSummary={workbenchStore.caseSummaryData}
                            cedent={cedent}
                            claimant={claimant}
                            error={workbenchStore.caseSummaryWidget.error}
                            isLoading={
                                workbenchStore.caseSummaryWidget.isLoading
                            }
                            mainClass={mainClass}
                            subject={subject}
                        />
                    </Col>
                    <Col xs={24} xl={9}>
                        <Space
                            className="workbench-new-subject-side-stack"
                            direction="vertical"
                            size={18}
                        >
                            <Card className="workbench-new-subject-card workbench-new-subject-action-panel">
                                <div className="workbench-new-subject-card-title">
                                    Next step
                                </div>
                                <div className="workbench-new-subject-action-list">
                                    <Button
                                        className="workbench-new-subject-action-row workbench-new-subject-action-row-primary"
                                        icon={
                                            <span className="workbench-new-subject-action-glyph workbench-new-subject-action-glyph-task" />
                                        }
                                        onClick={noop}
                                        type="link"
                                    >
                                        <span className="workbench-new-subject-action-label">
                                            Create follow-up task
                                        </span>
                                    </Button>
                                    <Button
                                        className="workbench-new-subject-action-row"
                                        icon={
                                            <span className="workbench-new-subject-action-glyph workbench-new-subject-action-glyph-note" />
                                        }
                                        onClick={noop}
                                        type="link"
                                    >
                                        <span className="workbench-new-subject-action-label">
                                            Add quick note
                                        </span>
                                    </Button>
                                    <Button
                                        className="workbench-new-subject-action-row"
                                        disabled={!latestUploadedDocument}
                                        icon={
                                            <span className="workbench-new-subject-action-glyph workbench-new-subject-action-glyph-document" />
                                        }
                                        onClick={() => {
                                            if (latestUploadedDocument) {
                                                window.open(
                                                    getDocumentViewerUrl(
                                                        latestUploadedDocument,
                                                    ),
                                                    "_blank",
                                                    "noopener,noreferrer",
                                                );
                                            }
                                        }}
                                        type="link"
                                    >
                                        <span className="workbench-new-subject-action-label">
                                            Open latest document
                                        </span>
                                    </Button>
                                </div>
                            </Card>
                            {subject?.kind === "Contract" ? (
                                <WorkbenchNewActivity subjectId={subjectId} />
                            ) : (
                                <Card className="workbench-new-subject-card workbench-new-subject-activity-card">
                                    <div className="workbench-new-subject-card-header-row">
                                        <div className="workbench-new-subject-card-title workbench-new-subject-card-title-small">
                                            Latest activity
                                        </div>
                                        <Button type="link" onClick={noop}>
                                            Show all
                                        </Button>
                                    </div>
                                    <div className="workbench-new-subject-activity-list">
                                        {actions.slice(0, 3).map((action) => (
                                            <div
                                                className="workbench-new-subject-activity-row"
                                                key={action.actionEventId}
                                            >
                                                <span className="workbench-new-subject-activity-icon">
                                                    <FileTextOutlined />
                                                </span>
                                                <span className="workbench-new-subject-activity-copy">
                                                    <span className="workbench-new-subject-activity-title">
                                                        {action.actionName ||
                                                            "Activity"}
                                                    </span>
                                                    <span className="workbench-new-subject-activity-detail">
                                                        <Button
                                                            type="link"
                                                            onClick={noop}
                                                        >
                                                            {action.taskTitle}
                                                        </Button>
                                                    </span>
                                                </span>
                                                <span className="workbench-new-subject-activity-meta">
                                                    <time>
                                                        {formatActivityTime(
                                                            action.triggeredOn,
                                                        )}
                                                    </time>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            )}
                        </Space>
                    </Col>
                </Row>

                <section className="workbench-new-subject-tabs">
                    <WorkbenchNewSubjectTabs
                        onFactsChanged={() => {
                            void workbenchStore.refreshSubjectFacts(subjectId);
                        }}
                        subjectId={subjectId}
                        broker={broker}
                        documents={documents}
                        identifier={identifier}
                        onDocumentPreview={setPreviewDocument}
                        selectedDocumentPackageId={
                            previewDocument?.packageId ?? null
                        }
                        tasks={tasks}
                        timelineActions={actions}
                        title={title}
                    />
                </section>
                {previewDocument ? (
                    <WorkbenchNewDocumentPreviewDrawer
                        document={previewDocument}
                        onClose={() => setPreviewDocument(null)}
                        onDownload={workbenchStore.handleAttachmentDownload}
                    />
                ) : null}
            </Layout.Content>
        </Layout>
    );
};

export default observer(WorkbenchNewSubjectPage);
